import clsx from 'clsx';
import { formatDurationMs, formatPercent } from '../../utils/formatters';
import type { FormAnalytics as FormAnalyticsType } from '../../api/client';

interface FormAnalyticsProps {
  form: FormAnalyticsType;
}

export default function FormAnalytics({ form }: FormAnalyticsProps) {
  function getDropoffColor(rate: number): string {
    if (rate > 20) return 'text-red-600 bg-red-50';
    if (rate > 10) return 'text-orange-600 bg-orange-50';
    return 'text-gray-600';
  }

  function getRefillColor(refills: number): string {
    if (refills > 50) return 'text-red-600';
    if (refills > 20) return 'text-orange-600';
    return 'text-gray-600';
  }

  return (
    <div className="card">
      <h3 className="mb-4 text-sm font-semibold text-gray-900">Field-Level Analytics</h3>
      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="table-header">Field</th>
              <th className="table-header">Type</th>
              <th className="table-header text-right">Interactions</th>
              <th className="table-header text-right">Drop-offs</th>
              <th className="table-header text-right">Drop-off Rate</th>
              <th className="table-header text-right">Avg. Time</th>
              <th className="table-header text-right">Refills</th>
              <th className="table-header text-right">Left Blank</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {(form.fields || []).map((field, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="table-cell">
                  <div>
                    <p className="font-medium text-gray-900">{field.label || field.name || 'Field'}</p>
                    {field.label && field.label !== field.name && (
                      <p className="text-xs text-gray-400 font-mono">{field.name}</p>
                    )}
                  </div>
                </td>
                <td className="table-cell">
                  <span className="badge bg-gray-100 text-gray-600">{field.type || 'unknown'}</span>
                </td>
                <td className="table-cell text-right">{(field.interactions || 0).toLocaleString()}</td>
                <td className="table-cell text-right">{(field.dropoffs || 0).toLocaleString()}</td>
                <td className="table-cell text-right">
                  <span
                    className={clsx(
                      'rounded px-1.5 py-0.5 text-xs font-medium',
                      getDropoffColor(field.dropoffRate || 0),
                    )}
                  >
                    {formatPercent(field.dropoffRate || 0)}
                  </span>
                </td>
                <td className="table-cell text-right text-gray-500">
                  {formatDurationMs(field.avgTimeSpent || 0)}
                </td>
                <td className="table-cell text-right">
                  <span className={getRefillColor(field.refills || 0)}>
                    {(field.refills || 0).toLocaleString()}
                  </span>
                </td>
                <td className="table-cell text-right">
                  <span className={(field.leftBlank || 0) > 10 ? 'text-orange-600' : 'text-gray-600'}>
                    {(field.leftBlank || 0).toLocaleString()}
                  </span>
                </td>
              </tr>
            ))}
            {(!form.fields || form.fields.length === 0) && (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">
                  No field data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
